import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, User, X, Shield } from "lucide-react";
import Tx from "@/components/Tx";
import TitleBadge from "@/components/TitleBadge";

/**
 * UserQuickMenu — tap a user (avatar / @username / online list) and a tiny
 * floating action sheet pops up with "View profile" + "Send message".
 *
 * Usage:
 *   const { open } = useUserQuickMenu();
 *   <button onClick={(e) => open(e, { username: "fanboy" })}>...</button>
 *
 * Mount <UserQuickMenuPortal /> once near the top of your tree.
 */

let externalOpen = null;

export function openUserQuickMenu(anchor, user) {
  if (externalOpen) externalOpen(anchor, user);
}

export default function UserQuickMenuPortal() {
  const [state, setState] = useState(null); // { user, top, left } or null
  const sheetRef = useRef(null);

  useEffect(() => {
    externalOpen = (anchor, user) => {
      if (!user || !user.username) return;
      const rect = anchor?.getBoundingClientRect ? anchor.getBoundingClientRect() : null;
      setState({
        user,
        top: rect ? Math.min(window.innerHeight - 180, rect.bottom + 8) : window.innerHeight / 2,
        left: rect ? Math.min(window.innerWidth - 220, rect.left) : window.innerWidth / 2 - 110,
      });
    };
    return () => { externalOpen = null; };
  }, []);

  useEffect(() => {
    if (!state) return;
    const onDoc = (e) => { if (sheetRef.current && !sheetRef.current.contains(e.target)) setState(null); };
    const onEsc = (e) => { if (e.key === "Escape") setState(null); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [state]);

  if (!state) return null;
  const { user, top, left } = state;
  return (
    <div className="fixed inset-0 z-[70]" data-testid="user-quick-menu">
      <div className="absolute inset-0 bg-black/30 sm:bg-transparent" onClick={() => setState(null)} />
      <div
        ref={sheetRef}
        className="absolute bg-[#041A3A] border border-[#00AFFF]/30 rounded-2xl shadow-2xl w-[220px] overflow-hidden animate-fadeIn"
        style={{ top, left }}
      >
        <div className="px-3 py-2.5 border-b border-white/10 flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-[#00AFFF]/20 border border-[#00AFFF]/30 grid place-items-center text-[10px] font-display font-black text-[#00AFFF] overflow-hidden">
            {user.avatar ? <img src={user.avatar} alt="" className="w-full h-full object-cover" /> : (user.username || "?").slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-display font-bold text-white truncate">@{user.username}</div>
            {user.rank && <div className="text-[10px] text-[#00AFFF] truncate">{user.rank}</div>}
          </div>
          <button onClick={() => setState(null)} className="ml-auto w-7 h-7 grid place-items-center rounded-full text-white/55 hover:text-white">
            <X size={13}/>
          </button>
        </div>
        <Link
          to={`/u/${user.username}`}
          data-testid="qmenu-profile"
          onClick={() => setState(null)}
          className="flex items-center gap-2 px-3 py-3 text-sm text-white hover:bg-[#00AFFF]/10 transition"
        >
          <User size={14} className="text-[#00AFFF]"/> <Tx>View profile</Tx>
        </Link>
        <Link
          to={`/messages?with=${user.username}`}
          data-testid="qmenu-dm"
          onClick={() => setState(null)}
          className="flex items-center gap-2 px-3 py-3 text-sm text-white hover:bg-[#00AFFF]/10 transition border-t border-white/5"
        >
          <MessageSquare size={14} className="text-[#00AFFF]"/> <Tx>Send message</Tx>
        </Link>
        {user.role === "admin" && (
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-[#F59E0B] border-t border-white/5 inline-flex items-center gap-1.5">
            <Shield size={10}/> <Tx>Site admin</Tx>
          </div>
        )}
      </div>
    </div>
  );
}
